// Voice recording. MediaRecorder directly — the runtime picks the first format
// the browser really supports, and everything ends up inside the protocol's
// mandatory minimum (audio/mp4 or audio/ogg;codecs=opus):
//
//   1. audio/mp4              — Safari always, newer Chromium often;
//   2. audio/ogg;codecs=opus  — Firefox natively;
//   3. audio/webm;codecs=opus — the Chromium fallback, remuxed losslessly to Ogg.
//
// A capture that comes back empty demotes its format for the rest of the
// session — some builds claim mp4 support and record nothing.

import { webmOpusToOgg } from './webm-to-ogg';

export interface VoiceRecording {
  bytes: Uint8Array;
  contentType: string;
  durationMs: number;
  /** 0–100 levels, at most 100 values — the spec-optional waveform. */
  waveform: number[];
}

interface FormatChoice {
  mime: string;
  kind: 'mp4' | 'ogg' | 'webm';
}

const CANDIDATES: FormatChoice[] = [
  { mime: 'audio/mp4', kind: 'mp4' },
  { mime: 'audio/ogg;codecs=opus', kind: 'ogg' },
  { mime: 'audio/webm;codecs=opus', kind: 'webm' },
];

/** Formats that produced an empty capture this session. */
const demoted = new Set<string>();

export function pickFormat(): FormatChoice | undefined {
  if (typeof MediaRecorder === 'undefined') {
    return undefined;
  }
  return CANDIDATES.find((c) => !demoted.has(c.mime) && MediaRecorder.isTypeSupported(c.mime));
}

export const recordingSupported = (): boolean => pickFormat() !== undefined;

export class VoiceRecorder {
  private stream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private choice: FormatChoice | null = null;
  private startedAtMs = 0;
  private audioContext: AudioContext | null = null;
  private levels: number[] = [];
  private levelTimer: ReturnType<typeof setInterval> | null = null;

  /** Throws DOMException NotAllowedError when the microphone is refused. */
  async start(): Promise<void> {
    const choice = pickFormat();
    if (choice === undefined) {
      throw new Error('no supported recording format');
    }
    this.choice = choice;
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.recorder = new MediaRecorder(this.stream, { mimeType: choice.mime });
    this.chunks = [];
    this.levels = [];
    this.recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        this.chunks.push(event.data);
      }
    };

    // Level metering for the waveform — cheap, optional, capped at 10 Hz.
    try {
      this.audioContext = new AudioContext();
      const source = this.audioContext.createMediaStreamSource(this.stream);
      const analyser = this.audioContext.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      const buffer = new Uint8Array(analyser.fftSize);
      this.levelTimer = setInterval(() => {
        analyser.getByteTimeDomainData(buffer);
        let sum = 0;
        for (let i = 0; i < buffer.length; i++) {
          const centered = (buffer[i]! - 128) / 128;
          sum += centered * centered;
        }
        const rms = Math.sqrt(sum / buffer.length);
        this.levels.push(Math.min(100, Math.round(rms * 300)));
      }, 100);
    } catch {
      // No waveform is fine; the element field is optional.
    }

    this.recorder.start(500);
    this.startedAtMs = Date.now();
  }

  get elapsedMs(): number {
    return this.startedAtMs === 0 ? 0 : Date.now() - this.startedAtMs;
  }

  /** Stops and converts. Returns undefined on an empty capture (format demoted —
   *  the caller may simply start again to use the next one). */
  async stop(): Promise<VoiceRecording | undefined> {
    const recorder = this.recorder;
    const choice = this.choice;
    if (recorder === null || choice === null) {
      this.cleanup();
      return undefined;
    }
    const durationMs = this.elapsedMs;
    await new Promise<void>((resolve) => {
      recorder.onstop = () => resolve();
      if (recorder.state === 'inactive') {
        resolve();
      } else {
        recorder.stop();
      }
    });
    this.cleanup();

    const blob = new Blob(this.chunks);
    const raw = new Uint8Array(await blob.arrayBuffer());
    if (raw.length === 0) {
      demoted.add(choice.mime);
      return undefined;
    }

    if (choice.kind === 'mp4') {
      return { bytes: raw, contentType: 'audio/mp4', durationMs, waveform: this.waveform() };
    }
    if (choice.kind === 'ogg') {
      return {
        bytes: raw,
        contentType: 'audio/ogg; codecs=opus',
        durationMs,
        waveform: this.waveform(),
      };
    }
    const ogg = webmOpusToOgg(raw);
    return {
      bytes: ogg,
      contentType: 'audio/ogg; codecs=opus',
      durationMs,
      waveform: this.waveform(),
    };
  }

  cancel(): void {
    if (this.recorder !== null && this.recorder.state !== 'inactive') {
      this.recorder.onstop = null;
      this.recorder.stop();
    }
    this.cleanup();
  }

  private waveform(): number[] {
    const source = this.levels;
    if (source.length <= 100) {
      return [...source];
    }
    const out: number[] = [];
    for (let i = 0; i < 100; i++) {
      out.push(source[Math.floor((i * source.length) / 100)]!);
    }
    return out;
  }

  private cleanup(): void {
    if (this.levelTimer !== null) {
      clearInterval(this.levelTimer);
      this.levelTimer = null;
    }
    void this.audioContext?.close().catch(() => undefined);
    this.audioContext = null;
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
    this.recorder = null;
  }
}
